import { useCallback, useEffect } from "react"
import { PhoneNumbers } from "../Contact/DialForm/PhoneNumbers/PhoneNumbers"
import { required } from "."
import { PhoneInput } from "./PhoneInput"
import { RegisterOptions, useFormContext, useWatch } from "react-hook-form"
import { useCopyToClipboard } from "../ContactHistory/MessageItem/useCopyToClipboard"
import { useContactInfoContext } from "../EmailCompose/hooks/useGetContactInfo"
import { useBoolean } from "@web/hooks/useBoolean"
import { GQL, normalizePhoneNumber } from "platform-common/index"

export interface PhoneInputWithPhoneNumbersProps {
  name: string
  placeholder?: string
  className?: string
  hidePhoneNumbers?: boolean
  required?: boolean
  rules?: RegisterOptions
  candidate?: GQL.CandidateOutput
  disabled?: boolean
  onBlur?(content: string | null): void
  onPhoneNumberPickedUp?(): void
}

export const PhoneInputWithPhoneNumbers = ({
  name,
  className,
  candidate,
  rules,
  onBlur,
  onPhoneNumberPickedUp,
  placeholder = "Phone Number",
  disabled: isDisabled = false,
  hidePhoneNumbers = false,
  required: isRequired = false,
}: PhoneInputWithPhoneNumbersProps) => {
  const { state: isPhoneNumberInjected, enable: setPhoneNumberInjected } = useBoolean(false)

  const { resetField, control } = useFormContext()

  const phoneNumber = useWatch({ control, name })

  const { copy: copyToClipboard } = useCopyToClipboard()
  const { isLoading: isLoadingContactInfo = false, lookedUpPhones } = useContactInfoContext() || {}

  useEffect(
    function injectLookedUpPhoneNumber() {
      if (!lookedUpPhones?.length) return

      const bestPhone = lookedUpPhones[0]

      if (!phoneNumber && !isPhoneNumberInjected) {
        resetField(name, { defaultValue: bestPhone?.phoneNumber })
        setPhoneNumberInjected()
        onPhoneNumberPickedUp?.()
      }
    },
    [
      lookedUpPhones,
      phoneNumber,
      resetField,
      isPhoneNumberInjected,
      name,
      onPhoneNumberPickedUp,
      setPhoneNumberInjected,
    ]
  )

  useEffect(
    function injectCandidatePhoneNumber() {
      if (!candidate) return

      const candidatePhoneNumber = candidate?.phoneNumbersFull?.[0]?.phoneNumber ?? ""
      const normalizedPhoneNumber = normalizePhoneNumber(candidatePhoneNumber) ?? candidatePhoneNumber

      if (!phoneNumber && !isPhoneNumberInjected) {
        resetField(name, { defaultValue: normalizedPhoneNumber })
        setPhoneNumberInjected()
        onPhoneNumberPickedUp?.()
      }
    },
    [candidate, phoneNumber, resetField, isPhoneNumberInjected, name, setPhoneNumberInjected, onPhoneNumberPickedUp]
  )

  const handlePhoneNumberClick = useCallback(
    async (phoneNumber: string) => {
      resetField(name, { defaultValue: phoneNumber })
      onPhoneNumberPickedUp?.()
      await copyToClipboard(phoneNumber)
    },
    [resetField, copyToClipboard, onPhoneNumberPickedUp, name]
  )

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>) => {
      onBlur?.(e.target.value)
    },
    [onBlur]
  )

  return (
    <div className="tmb-relative">
      <PhoneInput
        name={name}
        placeholder={placeholder}
        className={className}
        onBlur={handleBlur}
        rules={rules ?? isRequired ? { required: required("Phone Number") } : undefined}
        disabled={isDisabled}
      />
      {!hidePhoneNumbers && <PhoneNumbers isLoading={isLoadingContactInfo} onNumberClick={handlePhoneNumberClick} />}
    </div>
  )
}
